import { z } from 'zod';

// MyRewards training (TRN1, APP-076): five short lessons, a walkthrough of a practice copy of
// MyRewards made of sample data, and a three-question knowledge check; Sector Leads and their
// delegates get add-on lessons. The content is data, served by the server with the person's
// progress, so it can later be edited without touching the screen. The quiz's answer key stays on
// the server: an answer is checked when it's given.

const Key = z
  .string()
  .regex(/^[a-z][A-Za-z0-9]*$/)
  .max(40);
const Text = z.string().min(1).max(600);
const DateTime = z.iso.datetime({ offset: true });

/** What the walkthrough's coach mark says at one step. */
export const TrainingCoach = z.object({ title: Text, body: Text, tip: Text });
export type TrainingCoach = z.infer<typeof TrainingCoach>;

/**
 * One lesson, and its step in the walkthrough. The step shows a page of the practice copy and
 * points at one thing on it: a header item, the page's highlighted row, or Help.
 */
export const TrainingLesson = z.object({
  key: Key,
  title: Text,
  summary: Text,
  minutes: z.int().min(1).max(15),
  page: Key,
  pointsAt: z.enum(['home', 'rewards', 'recognize', 'help', 'row']),
  coach: TrainingCoach,
});
export type TrainingLesson = z.infer<typeof TrainingLesson>;

/** A row on a page of the practice copy: sample data, never a real award. */
export const PracticeRow = z.object({
  title: Text,
  meta: Text,
  action: Text,
  tag: z.object({ label: Text, tone: z.enum(['neutral', 'waiting', 'done', 'info']) }).nullable(),
  edge: z.enum(['gold', 'cyan', 'plain']),
});
export type PracticeRow = z.infer<typeof PracticeRow>;

/** One page of the practice copy. */
export const PracticePage = z.object({
  key: Key,
  eyebrow: Text,
  title: Text,
  /** The header item that's current on this page. */
  nav: z.enum(['home', 'rewards', 'recognize']),
  rows: z.array(PracticeRow).min(1).max(6),
  /** The row a step that points at `row` highlights. */
  highlight: z.int().nonnegative().nullable(),
});
export type PracticePage = z.infer<typeof PracticePage>;

/** A knowledge-check question without its answer: the server checks each answer. */
export const TrainingQuestion = z.object({
  key: Key,
  prompt: Text,
  options: z
    .array(z.object({ key: Key, label: Text }))
    .min(2)
    .max(5),
});
export type TrainingQuestion = z.infer<typeof TrainingQuestion>;

export const TrainingContent = z.object({
  /** Changes when the lessons or questions change (APP-076 §3). */
  version: z.string().min(1).max(40),
  /** The lessons everyone takes; finishing needs the knowledge check, not every lesson. */
  lessons: z.array(TrainingLesson).min(1),
  /** Add-on lessons for this person's roles (Sector Leads and delegates); optional. */
  addOns: z.array(TrainingLesson),
  pages: z.array(PracticePage).min(1),
  questions: z.array(TrainingQuestion).min(1),
});
export type TrainingContent = z.infer<typeof TrainingContent>;

/** A question's latest answer, checked by the server. */
export const TrainingAnswer = z.object({
  question: Key,
  option: Key,
  right: z.boolean(),
  /** What the check says about it ("Letters stay with each award in My rewards."). */
  feedback: Text,
});
export type TrainingAnswer = z.infer<typeof TrainingAnswer>;

export const TrainingProgress = z.object({
  /** Lessons and add-ons done, in the order they were done. */
  lessonsDone: z.array(Key),
  answers: z.array(TrainingAnswer),
  completedAt: DateTime.nullable(),
  startedAt: DateTime.nullable(),
  /** How many times the person started over. */
  resets: z.int().nonnegative(),
});
export type TrainingProgress = z.infer<typeof TrainingProgress>;

/** `GET /api/v1/training`: the content for this person, and their progress. */
export const TrainingView = z.object({
  /** The organization's today, for "Completed today". */
  asOf: z.iso.date(),
  content: TrainingContent,
  progress: TrainingProgress,
  /** False in Preview: the walkthrough runs, nothing is saved. */
  canSave: z.boolean(),
});
export type TrainingView = z.infer<typeof TrainingView>;

/** `POST /api/v1/training/lessons`: a lesson's walkthrough step finished. */
export const TrainingLessonDone = z.object({ lesson: Key }).strict();
export type TrainingLessonDone = z.infer<typeof TrainingLessonDone>;

/** `POST /api/v1/training/answers`: one answer to the knowledge check. */
export const TrainingAnswerGiven = z.object({ question: Key, option: Key }).strict();
export type TrainingAnswerGiven = z.infer<typeof TrainingAnswerGiven>;

/** `POST /api/v1/training/complete` and `/reset` take an empty body. */
export const TrainingCommand = z.object({}).strict();
export type TrainingCommand = z.infer<typeof TrainingCommand>;
